import { useNavigate } from 'react-router-dom';
import {
  Building,
  Calendar,
  CardIdentity,
  ClockIcon,
  DocumentIcon,
  Lightning,
  MissionIcon,
  PersonIcon,
  ProfilIcon,
  StarOutline,
  Vivier,
} from 'assets/icons';
import CompactEmptyState from 'components/EmptyState/CompactEmptyState';

// The icon of the matching sidebar entry, so the empty state names the
// section it belongs to. Types left out get CompactEmptyState's document.
const ICONS = {
  mission_done: MissionIcon,
  mission_in_progress: MissionIcon,
  mission_soon: MissionIcon,
  mission_search: MissionIcon,
  mission_search_dashboard: MissionIcon,
  select_mission: MissionIcon,
  company: Building,
  contract: DocumentIcon,
  time_sheet: Calendar,
  time_sheet_to_be_completed: Calendar,
  time_sheet_to_be_checked: Calendar,
  time_sheet_litigation: Calendar,
  time_sheet_checked: Calendar,
  schedule: Calendar,
  schedule_missing_periods: Calendar,
  payslip: DocumentIcon,
  invoice: DocumentIcon,
  coming_soon: ClockIcon,
  log_vivier: Vivier,
  mission_invitation: PersonIcon,
  avis_clients: StarOutline,
  identity_validation: CardIdentity,
  qualification: Lightning,
  no_profile_assigned: ProfilIcon,
};

const TITLES = {
  mission_done: 'Aucune mission terminées',
  mission_in_progress: 'Aucune mission en cours',
  mission_soon: 'Aucune mission prochainement',
  mission_search: 'Pas de missions disponibles',
  company: 'Aucune entreprise trouvée',
  contract: 'Aucun contrat signé disponible',
  time_sheet: 'Aucun relevé d’heure à compléter',
  payslip: 'Aucune fiche de paie',
  coming_soon: 'Fonctionnalité bientôt disponible',
  select_mission: 'Sélectionnez une mission',
  log_vivier: 'Aucune notification de vivier',
  schedule_missing_periods: 'Période manquante',
  schedule: 'Nouvelle fonctionnalité Planning',
  mission_invitation: 'Aucun intérimaire à inviter',
  identity_validation: 'Aucun utilisateur à valider',
  qualification: 'Aucune qualification trouvée',
  avis_clients: 'Aucun avis client',
  time_sheet_to_be_completed: 'Aucun relevé non complété',
  time_sheet_to_be_checked: 'Aucun relevé à vérifier',
  time_sheet_litigation: 'Aucun relevé en litige',
  time_sheet_checked: 'Aucun relevé vérifié',
  no_profile_assigned: 'Aucun profil attribué',
};

const DESCRIPTIONS = {
  mission_done:
    'Oops, vous n’avez pas encore de missions terminées, pour en avoir il faut postuler à une mission !',
  mission_in_progress:
    'Oops, vous n’avez pas encore de missions en cours, pour en avoir il faut postuler à une mission !',
  mission_soon:
    "Oops, vous n'avez pas encore de missions prochainement, pour en avoir, il faut postuler à une mission !",
  mission_search:
    'Oops, il n’y a pas encore de missions disponibles pour le moment, un peu de patience',
  company:
    'Aucune entreprise ne correspond à votre recherche ou à vos filtres. Modifiez vos critères ou ajoutez une nouvelle entreprise.',
  contract:
    'Oops, il n’y a pas encore de missions disponibles pour le moment, un peu de patience',
  time_sheet:
    'Oops, il n’y a pas encore de missions disponibles pour le moment, un peu de patience',
  payslip:
    'Les fiches de paie du mois apparaîtront ici dès qu’elles seront importées. Changez de mois pour en voir d’autres.',
  coming_soon:
    'Vous serez notifié une fois que cette fonctionnalité sera disponible.',
  select_mission: 'Sélectionnez une mission pour exécuter une action.',
  log_vivier:
    'Oops, il n’y a pas encore de notification de viviers pour le moment.',
  schedule_missing_periods:
    'Ajoutez une date de début et de fin pour pouvoir éditer le planning de la mission',
  schedule:
    "Activez la fonctionnalité planning pour un suivi clair et efficace de vos missions d'intérim.",
  mission_invitation: "Pas d'intérimaire à inviter pour le moment",
  identity_validation:
    "Il n'y a pas encore d'utilisateurs en attente de validation d'identité.",
  qualification:
    "Il n'y a pas encore de qualifications disponibles. Créez-en une pour commencer.",
  avis_clients: "Il n'y a pas encore d'avis clients à traiter pour le moment.",
  time_sheet_to_be_completed:
    "Aucun relevé d'heures n'est en attente de complétion par l'intérimaire pour cette période.",
  time_sheet_to_be_checked:
    "Aucun relevé d'heures n'est en attente de vérification pour cette période.",
  time_sheet_litigation:
    "Aucun relevé d'heures n'est en litige pour cette période.",
  time_sheet_checked:
    "Aucun relevé d'heures n'a été vérifié pour cette période.",
  no_profile_assigned: 'Vous devez attribuer un profil à cette mission.',
};

// Types that come with their own button; an `action` prop replaces it.
const NAVIGATIONS = {
  mission_done: { label: 'Rechercher de missions', path: '/searchMission' },
  mission_in_progress: {
    label: 'Rechercher de missions',
    path: '/searchMission',
  },
  mission_soon: { label: 'Rechercher de missions', path: '/searchMission' },
};

/**
 * The empty state of a screen, in the one layout of the site
 * (CompactEmptyState, mockup « 1.5 · Liste – Aucune facture sur la période »).
 * `type` picks the icon, the title, the text and sometimes a button;
 * `title`, `description` and `action` ({ label, onClick }) override them.
 */
const EmptyState = ({
  type,
  title: titleOverride,
  description: descriptionOverride,
  action,
}) => {
  const navigate = useNavigate();

  const Icon = ICONS[type] ?? DocumentIcon;
  const navigation = NAVIGATIONS[type];
  const resolvedAction =
    action ??
    (navigation && {
      label: navigation.label,
      onClick: () => navigate(navigation.path),
    });

  return (
    <CompactEmptyState
      icon={<Icon wh={20} color="#374151" />}
      title={titleOverride ?? TITLES[type]}
      description={descriptionOverride ?? DESCRIPTIONS[type]}
      action={resolvedAction || undefined}
    />
  );
};

export default EmptyState;
